import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useCallback, useEffect, useState } from "react";
import PublicLayout from "@layouts/public/PublicLayout";
import GetBlogPostListings from "@modules/blog/api/getPostBlogListings";
import CmsBlogPostListing from "@modules/blog/types/BlogPostListing";
import useEmblaCarousel from "embla-carousel-react";
import { CmsButtonType } from "@talladega/types";
import { useTranslation } from "next-i18next";
import BlogPageCard from "@modules/blog/components/BlogPageCard";
import { Button, CarouselNavigation } from "@talladega/components";
import BlogHeroHeading from "@modules/blog/components/BlogHeroHeading";
import { CmsLayout } from "@modules/cms/types/CmsLayout";
import { GetStaticPropsContext } from "next";
import CmsLayouts from "@modules/cms/components/CmsLayouts";
import { useRouter } from "next/router";
import getCmsPageData from "@modules/cms/api/getCmsPageData";
import SeoInfo from "@common/components/SeoInfo";
import {
  gtmPushData,
  getCanonicalUrl,
  readingTime,
  ReadingTimeResult,
  dateTimeFormatter,
} from "@talladega/helpers";
import getCmsTextContents from "@modules/cms/helpers/getCmsTextContents";
import useHappyFox from "@modules/happyfox/useHappyFox";
import { getFeatureFlagList } from "@modules/feature-flags/hooks/useFeatureFlags";
import { FeatureFlags } from "@modules/feature-flags/types/FeatureFlag";

type BlogPostListing = Omit<CmsBlogPostListing, "layouts"> & {
  readingTime: ReadingTimeResult;
};

type Props = {
  blogPosts: BlogPostListing[];
  blogLayouts: CmsLayout[];
  consumerFinancingEnabled?: boolean;
};

const blogHeroImage = (blogPost: BlogPostListing) => {
  return {
    id: "blog-hero-image",
    sourceUrl: blogPost?.featuredImage?.node?.sourceUrl,
    altText: "blog-hero-image",
  };
};

const blogAvatar = (blogPost: BlogPostListing) => {
  return {
    avatarImage: {
      id: "blog-avatar",
      link: blogPost?.author?.node?.avatar?.url,
      altText: "blog-avatar",
    },
    avatarName: blogPost?.author?.node?.name,
  };
};

const blogPostReadingTime = (blogPost: CmsBlogPostListing) => {
  const cmsTextContents = getCmsTextContents(blogPost.layouts || []);

  return readingTime(cmsTextContents);
};

const BlogsPage = ({
  blogPosts,
  blogLayouts,
  consumerFinancingEnabled,
}: Props) => {
  const { t } = useTranslation(["blogsPage", "common"]);
  const { locale } = useRouter();

  useHappyFox();

  const buttonField = (blogPost: BlogPostListing): CmsButtonType => {
    const postLink = `/blog/${blogPost.slug}`;
    return {
      isHidden: null,
      isButtonLink: true,
      buttonLink: {
        target: "",
        title: t("common:read_article"),
        url: postLink,
      },
      buttonType: "filled",
      buttonStyleFilled: "primary",
      buttonStyleOutline: "primary",
      buttonStyleLink: "primary",
      formData: null,
    };
  };

  const blogHeroHeadingSubLabel = (blogPost: BlogPostListing) => {
    const formattedDate = dateTimeFormatter(
      blogPost?.date ? new Date(blogPost?.date) : null,
      locale || "en",
      "MMMM D, YYYY"
    );
    const readingTimeDisplay = t("common:reading_time", {
      minute: blogPost.readingTime.minutes,
    });

    return (
      <div className="mt-4 flex items-center justify-start gap-2 lg:mt-8">
        <h4>{formattedDate}</h4>
        <span>•</span>
        <h4 className="italic">{readingTimeDisplay}</h4>
      </div>
    );
  };

  const blogPageCardHeroSubLabel = (blogPost: BlogPostListing) => {
    const formattedDate = dateTimeFormatter(
      new Date(blogPost?.date),
      locale || "en",
      "MMMM D, YYYY"
    );
    const readingTimeDisplay = t("common:reading_time", {
      minute: blogPost.readingTime.minutes,
    });

    return (
      <p className="flex items-center justify-start gap-2 text-sm font-semibold italic text-gray-500">
        <span>{formattedDate}</span>
        <span>•</span>
        <span>{readingTimeDisplay}</span>
      </p>
    );
  };

  const [emblaRef, embla] = useEmblaCarousel({
    align: "start",
    active: blogPosts && blogPosts.length > 1,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
  }, [embla, setSelectedIndex]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on("select", onSelect);
  }, [embla, onSelect]);

  const scrollPrev = useCallback(() => {
    gtmPushData({
      event: "News",
      element: "Slider",
      descriptor: "Previous",
    });
    return embla && embla.scrollPrev();
  }, [embla]);
  const scrollNext = useCallback(() => {
    gtmPushData({
      event: "News",
      element: "Slider",
      descriptor: "Next",
    });
    return embla && embla.scrollNext();
  }, [embla]);
  const scrollTo = useCallback(
    (index: number) => embla && embla.scrollTo(index),
    [embla]
  );

  const [pageSize, setPageSize] = useState(6);
  const [showLoadMore, setShowLoadMore] = useState(false);

  const loadMoreArticles = () => {
    gtmPushData({
      event: "News",
      element: "Action",
      descriptor: "Load-More",
    });
    setPageSize(pageSize + 18);
  };

  const featuredBlogPosts: BlogPostListing[] = [];
  const nonFeaturedBlogPosts: BlogPostListing[] = [];

  blogPosts.forEach((element) => {
    if (
      element.categories?.nodes.some(
        (x) => x.name === "Featured" || x.name === "Présenté"
      )
    ) {
      featuredBlogPosts.push(element);
    }
  });

  blogPosts.forEach((element) => {
    if (
      !element.categories?.nodes.some(
        (x) => x.name === "Featured" || x.name === "Présenté"
      )
    ) {
      nonFeaturedBlogPosts.push(element);
    }
  });

  useEffect(() => {
    if (pageSize < nonFeaturedBlogPosts.length) {
      setShowLoadMore(true);
    } else {
      setShowLoadMore(false);
    }
  }, [blogPosts.length, nonFeaturedBlogPosts.length, pageSize]);

  return (
    <PublicLayout consumerFinancingEnabled={consumerFinancingEnabled}>
      <SeoInfo
        seoInfo={{
          title: t("blogsPage:blog_title_tag"),
          metaDesc: t("blogsPage:blog"),
          opengraphTitle: t("blogsPage:blog_og_title"),
          opengraphType: "website",
          opengraphDescription: t("blogsPage:blog_og_description"),
          opengraphImage: {
            sourceUrl:
              blogPosts[0] && blogPosts[0]?.featuredImage
                ? blogPosts[0]?.featuredImage?.node?.sourceUrl
                : "",
          },
          canonical: getCanonicalUrl("blog"),
        }}
      />
      <div>
        <div className="bg-white">
          <div className="mx-auto w-full max-w-page">
            <div className="overflow-hidden" ref={emblaRef}>
              <div className="flex">
                {featuredBlogPosts &&
                  featuredBlogPosts.length > 0 &&
                  featuredBlogPosts.slice(0, 5).map((blogPost) => (
                    <div
                      className="mr-2 h-auto flex-[0_0_100%] overflow-hidden"
                      key={blogPost.postId}
                    >
                      <BlogHeroHeading
                        heroLabel={blogPost.title}
                        heroSubLabel={blogHeroHeadingSubLabel(blogPost)}
                        heroTopSubLabel={
                          blogPost?.categories?.nodes.find(
                            (x) =>
                              x.name !== "Featured" && x.name !== "Présenté"
                          )?.name
                        }
                        heroImage={blogHeroImage(blogPost)}
                        imagePosition="Right"
                        backgroundColor="bg-white"
                        heroAvatar={blogAvatar(blogPost)}
                        button={buttonField(blogPost)}
                      />
                    </div>
                  ))}
                {(!featuredBlogPosts ||
                  (featuredBlogPosts && featuredBlogPosts.length === 0)) &&
                  blogPosts &&
                  blogPosts.slice(0, 1).map((blogPost) => (
                    <div
                      className="mr-2 h-auto flex-[0_0_100%] overflow-hidden"
                      key={blogPost.postId}
                    >
                      <BlogHeroHeading
                        heroLabel={blogPost.title}
                        heroSubLabel={blogHeroHeadingSubLabel(blogPost)}
                        heroTopSubLabel={
                          blogPost?.categories?.nodes.find(
                            (x) =>
                              x.name !== "Featured" && x.name !== "Présenté"
                          )?.name
                        }
                        heroImage={blogHeroImage(blogPost)}
                        imagePosition="Right"
                        backgroundColor="bg-white"
                        heroAvatar={blogAvatar(blogPost)}
                        button={buttonField(blogPost)}
                      />
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
        <div className="my-9 items-center justify-center text-center">
          {featuredBlogPosts && featuredBlogPosts.length > 0 && (
            <CarouselNavigation
              buttonColours="secondary"
              contentList={featuredBlogPosts.slice(0, 5)}
              scrollNext={scrollNext}
              scrollPrev={scrollPrev}
              scrollTo={scrollTo}
              selectedIndex={selectedIndex}
              hiddenOnMobile
            />
          )}

          {(!featuredBlogPosts ||
            (featuredBlogPosts && featuredBlogPosts.length === 0)) &&
            blogPosts && (
              <CarouselNavigation
                buttonColours="secondary"
                contentList={blogPosts.slice(0, 5)}
                scrollNext={scrollNext}
                scrollPrev={scrollPrev}
                scrollTo={scrollTo}
                selectedIndex={selectedIndex}
                hiddenOnMobile
              />
            )}
        </div>

        <div className="mx-auto max-w-page px-4 pb-6 pt-10 lg:px-5">
          <div className="mt-6 grid grid-flow-row grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 md:grid-cols-2 xl:grid-cols-3 xl:gap-x-8">
            {featuredBlogPosts &&
              featuredBlogPosts.length > 0 &&
              nonFeaturedBlogPosts
                .slice(0, pageSize)
                .map((blogPost) => (
                  <BlogPageCard
                    key={blogPost.postId}
                    heroLabel={blogPost.title}
                    heroSubLabel={blogPageCardHeroSubLabel(blogPost)}
                    heroTopSubLabel={blogPost?.categories?.nodes[0]?.name}
                    heroImage={blogHeroImage(blogPost)}
                    imagePosition="Right"
                    heroAvatar={blogAvatar(blogPost)}
                    button={buttonField(blogPost)}
                  />
                ))}

            {(!featuredBlogPosts ||
              (featuredBlogPosts && featuredBlogPosts.length === 0)) &&
              blogPosts
                .slice(1, pageSize)
                .map((blogPost) => (
                  <BlogPageCard
                    key={blogPost.postId}
                    heroLabel={blogPost.title}
                    heroSubLabel={blogPageCardHeroSubLabel(blogPost)}
                    heroTopSubLabel={blogPost?.categories?.nodes[0]?.name}
                    heroImage={blogHeroImage(blogPost)}
                    imagePosition="Right"
                    heroAvatar={blogAvatar(blogPost)}
                    button={buttonField(blogPost)}
                  />
                ))}
          </div>
        </div>

        {showLoadMore && (
          <div className="mx-auto w-56 py-12">
            <Button
              buttonStyle="primary"
              fill="outline"
              spacing="fixed"
              onClick={() => loadMoreArticles()}
            >
              {t("common:load_more_articles")}
            </Button>
          </div>
        )}

        {/** Blog Layouts */}
        <div>
          <CmsLayouts layouts={blogLayouts ?? []} />
        </div>
      </div>
    </PublicLayout>
  );
};

BlogsPage.requiresLoggedInUser = false;
BlogsPage.pageName = "BlogsPage";

export async function getStaticProps({
  preview,
  locale,
}: GetStaticPropsContext) {
  const data = await GetBlogPostListings(locale?.toUpperCase());

  const { layouts } = await getCmsPageData(
    "blog-page",
    preview,
    locale?.toUpperCase()
  );

  const blogPostsWithFeaturedImage = data
    ? data.filter((x) => x.featuredImage != null)
    : [];

  const blogPosts: BlogPostListing[] = blogPostsWithFeaturedImage.map(
    (blogPost) => {
      const { layouts: _, ...fields } = blogPost;
      return { ...fields, readingTime: blogPostReadingTime(blogPost) };
    }
  );

  const featureFlagList = await getFeatureFlagList();

  return {
    props: {
      blogLayouts: layouts,
      ...(await serverSideTranslations(locale ?? "en", [
        "blogsPage",
        "common",
        "common-components",
        "purchase",
      ])),
      consumerFinancingEnabled: featureFlagList.some(
        (f) => f === FeatureFlags.C2CFinancing
      ),
      blogPosts,
    },
    revalidate: 60,
  };
}

export default BlogsPage;
